/**
 * @architecture-header 1
 * @layer module-infrastructure
 * @domain settings-invitations
 * @responsibility Serialize the SET-F06 Invitations-tab wire calls onto the identity_access
 *   organization-invitation operations (admin-side create/list/resend/revoke; acceptance-side
 *   preview/accept/decline).
 * @state-owner authenticated API
 * @allowed-imports API client public type; @vispo/contracts
 * @forbidden React; UI role inference; email/provider adapters
 * @public-api listOrganizationInvitations, createOrganizationInvitation,
 *   resendOrganizationInvitation, revokeOrganizationInvitation, previewOrganizationInvitation,
 *   acceptOrganizationInvitation, declineOrganizationInvitation
 * @side-effects authenticated and unauthenticated HTTP requests
 * @tests frontend/packages/workspace-web/src/modules/settings-invitations/components/InvitationsSettingsSurface.test.tsx
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 */

import type { VispoApiClient } from '@vispo/api-client';
import {
  type OrganizationInvitationAcceptInput,
  type OrganizationInvitationCreateInput,
  type OrganizationInvitationPreviewInput,
  type OrganizationInvitationRevisionInput,
  organizationInvitationAcceptInputSchema,
  organizationInvitationAcceptResponseSchema,
  organizationInvitationCommandResponseSchema,
  organizationInvitationCreateInputSchema,
  organizationInvitationListResponseSchema,
  organizationInvitationPreviewInputSchema,
  organizationInvitationPreviewResponseSchema,
  organizationInvitationRevisionInputSchema,
} from '@vispo/contracts';

const invitationsPath = (organizationId: string) =>
  `/api/v1/organizations/${encodeURIComponent(organizationId)}/invitations`;

// Acceptance-side routes are deliberately unprefixed by an organization id -- the token alone
// resolves the invitation and its organization (mirrors CL-F03's acceptance_router).
const acceptancePath = (suffix = '') => `/api/v1/organization-invitations${suffix}`;

export function listOrganizationInvitations(
  apiClient: VispoApiClient,
  organizationId: string,
  status: string,
) {
  return apiClient
    .request<unknown>(`${invitationsPath(organizationId)}?status=${encodeURIComponent(status)}`)
    .then((response) => organizationInvitationListResponseSchema.parse(response));
}

export function createOrganizationInvitation(
  apiClient: VispoApiClient,
  organizationId: string,
  input: OrganizationInvitationCreateInput,
) {
  return apiClient
    .request<unknown>(invitationsPath(organizationId), {
      method: 'POST',
      body: organizationInvitationCreateInputSchema.parse(input),
    })
    .then((response) => organizationInvitationCommandResponseSchema.parse(response));
}

export function resendOrganizationInvitation(
  apiClient: VispoApiClient,
  organizationId: string,
  invitationId: string,
  input: OrganizationInvitationRevisionInput,
) {
  return apiClient
    .request<unknown>(
      `${invitationsPath(organizationId)}/${encodeURIComponent(invitationId)}/resend`,
      {
        method: 'POST',
        body: organizationInvitationRevisionInputSchema.parse(input),
      },
    )
    .then((response) => organizationInvitationCommandResponseSchema.parse(response));
}

export function revokeOrganizationInvitation(
  apiClient: VispoApiClient,
  organizationId: string,
  invitationId: string,
  input: OrganizationInvitationRevisionInput,
) {
  return apiClient
    .request<unknown>(`${invitationsPath(organizationId)}/${encodeURIComponent(invitationId)}`, {
      method: 'DELETE',
      body: organizationInvitationRevisionInputSchema.parse(input),
    })
    .then((response) => organizationInvitationCommandResponseSchema.parse(response));
}

export function previewOrganizationInvitation(
  apiClient: VispoApiClient,
  input: OrganizationInvitationPreviewInput,
) {
  return apiClient
    .request<unknown>(acceptancePath('/preview'), {
      method: 'POST',
      body: organizationInvitationPreviewInputSchema.parse(input),
    })
    .then((response) => organizationInvitationPreviewResponseSchema.parse(response));
}

export function acceptOrganizationInvitation(
  apiClient: VispoApiClient,
  invitationId: string,
  input: OrganizationInvitationAcceptInput,
) {
  return apiClient
    .request<unknown>(acceptancePath(`/${encodeURIComponent(invitationId)}/accept`), {
      method: 'POST',
      body: organizationInvitationAcceptInputSchema.parse(input),
    })
    .then((response) => organizationInvitationAcceptResponseSchema.parse(response));
}

export function declineOrganizationInvitation(
  apiClient: VispoApiClient,
  invitationId: string,
  input: OrganizationInvitationAcceptInput,
) {
  return apiClient.request<unknown>(
    acceptancePath(`/${encodeURIComponent(invitationId)}/decline`),
    {
      method: 'POST',
      body: organizationInvitationAcceptInputSchema.parse(input),
    },
  );
}
